import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const AvailabilityForm = ({ formData, onUpdate, onNext, onPrevious }) => {
  const [errors, setErrors] = useState({});
  const [selectedDates, setSelectedDates] = useState(formData.unavailableDates || []);
  const [uploadedFiles, setUploadedFiles] = useState(formData.unavailableDateFiles || []);
  const fileInputRef = useRef(null);

  const daysOfWeek = [
    { id: 'monday', label: 'Monday', short: 'Mon' },
    { id: 'tuesday', label: 'Tuesday', short: 'Tue' },
    { id: 'wednesday', label: 'Wednesday', short: 'Wed' },
    { id: 'thursday', label: 'Thursday', short: 'Thu' },
    { id: 'friday', label: 'Friday', short: 'Fri' },
    { id: 'saturday', label: 'Saturday', short: 'Sat' },
    { id: 'sunday', label: 'Sunday', short: 'Sun' }
  ];

  const bookingSettings = [
    {
      id: 'instant',
      label: 'Instant Booking',
      description: 'Couples can book immediately without approval',
      icon: 'Zap'
    },
    {
      id: 'request',
      label: 'Request to Book',
      description: 'You approve each booking request manually',
      icon: 'MessageSquare'
    },
    {
      id: 'consultation',
      label: 'Consultation Required',
      description: 'Schedule a consultation before booking',
      icon: 'Calendar'
    }
  ];

  const leadTimeOptions = [
    { value: '1', label: '1 day' },
    { value: '3', label: '3 days' },
    { value: '7', label: '1 week' },
    { value: '14', label: '2 weeks' },
    { value: '30', label: '1 month' },
    { value: '60', label: '2 months' },
    { value: '90', label: '3 months' }
  ];

  const advanceBookingOptions = [
    { value: '30', label: '1 month' },
    { value: '90', label: '3 months' },
    { value: '180', label: '6 months' },
    { value: '365', label: '1 year' },
    { value: '730', label: '2 years' },
    { value: 'unlimited', label: 'No limit' }
  ];

  // Generate calendar for next 12 months (extended from 3 months)
  const generateCalendarDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 365; i++) { // Extended to 12 months
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        date: date.toISOString().split('T')[0],
        day: date.getDate(),
        month: date.getMonth(),
        year: date.getFullYear(),
        dayOfWeek: date.getDay(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6
      });
    }
    
    return dates;
  };

  const calendarDates = generateCalendarDates();

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleArrayToggle = (field, value) => {
    const currentArray = formData[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    onUpdate({ [field]: newArray });
  };

  const handleDateToggle = (dateString) => {
    const newDates = selectedDates.includes(dateString)
      ? selectedDates.filter(date => date !== dateString)
      : [...selectedDates, dateString];
    
    setSelectedDates(newDates);
    onUpdate({ unavailableDates: newDates });
  };

  const removeDateFromSelection = (dateToRemove) => {
    const newDates = selectedDates.filter(date => date !== dateToRemove);
    setSelectedDates(newDates);
    onUpdate({ unavailableDates: newDates });
  };

  // Fixed file upload functionality
  const handleFileUpload = (files) => {
    const newFiles = Array.from(files).map((file, index) => ({
      id: Date.now() + index,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadedAt: new Date().toISOString()
    }));
    
    const updatedFiles = [...uploadedFiles, ...newFiles];
    setUploadedFiles(updatedFiles);
    onUpdate({ unavailableDateFiles: updatedFiles });
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeFile = (fileId) => {
    const updatedFiles = uploadedFiles.filter(file => file.id !== fileId);
    setUploadedFiles(updatedFiles);
    onUpdate({ unavailableDateFiles: updatedFiles });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.bookingType) {
      newErrors.bookingType = 'Please select your booking preference';
    }
    if (!formData.minimumLeadTime) {
      newErrors.minimumLeadTime = 'Please select minimum lead time';
    }
    if (!formData.advanceBooking) {
      newErrors.advanceBooking = 'Please select advance booking limit';
    }
    if (!formData.availableDays?.length) {
      newErrors.availableDays = 'Please select at least one available day';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const getMonthName = (monthIndex) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthIndex];
  };

  // Group dates by month
  const datesByMonth = calendarDates.reduce((acc, date) => {
    const monthKey = `${date.year}-${date.month}`;
    if (!acc[monthKey]) {
      acc[monthKey] = {
        month: date.month,
        year: date.year,
        dates: []
      };
    }
    acc[monthKey].dates.push(date);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      {/* Booking Preferences */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Booking Preferences
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          How would you like to handle booking requests from couples?
        </p>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {bookingSettings.map((setting) => (
            <button
              key={setting.id}
              onClick={() => handleInputChange('bookingType', setting.id)}
              className={`p-6 rounded-xl border-2 text-left transition-smooth hover:shadow-soft ${
                formData.bookingType === setting.id
                  ? 'border-primary bg-primary-50 shadow-soft'
                  : 'border-border hover:border-primary-200 bg-white'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                  formData.bookingType === setting.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-surface-100 text-text-secondary'
                }`}>
                  <Icon name={setting.icon} size={20} color="currentColor" />
                </div>
                <div className="flex-1">
                  <h4 className={`font-medium text-sm ${
                    formData.bookingType === setting.id ? 'text-primary' : 'text-text-primary'
                  }`}>
                    {setting.label}
                  </h4>
                  <p className="text-xs text-text-muted font-caption mt-1">
                    {setting.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
        
        {errors.bookingType && (
          <p className="text-error text-sm font-caption mt-2 flex items-center space-x-1">
            <Icon name="AlertCircle" size={16} color="currentColor" />
            <span>{errors.bookingType}</span>
          </p>
        )}
      </div>

      {/* Availability Settings */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Availability Settings
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Set your booking timeline and available days
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Minimum Lead Time *
            </label>
            <select
              value={formData.minimumLeadTime || ''}
              onChange={(e) => handleInputChange('minimumLeadTime', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                errors.minimumLeadTime ? 'border-error' : 'border-border'
              }`}
            >
              <option value="">Select minimum lead time</option>
              {leadTimeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-muted font-caption mt-1">
              How far in advance do you need to be booked?
            </p>
            {errors.minimumLeadTime && (
              <p className="text-error text-sm font-caption mt-1">{errors.minimumLeadTime}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Advance Booking Limit *
            </label>
            <select
              value={formData.advanceBooking || ''}
              onChange={(e) => handleInputChange('advanceBooking', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary ${
                errors.advanceBooking ? 'border-error' : 'border-border'
              }`}
            >
              <option value="">Select advance booking limit</option>
              {advanceBookingOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-muted font-caption mt-1">
              How far in advance can couples book you?
            </p>
            {errors.advanceBooking && (
              <p className="text-error text-sm font-caption mt-1">{errors.advanceBooking}</p>
            )}
          </div>
        </div>
      </div>

      {/* Available Days */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Available Days
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Which days of the week are you typically available?
        </p>
        
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {daysOfWeek.map((day) => (
            <label
              key={day.id}
              className={`p-4 rounded-lg border-2 cursor-pointer text-center transition-smooth hover:shadow-soft ${
                formData.availableDays?.includes(day.id)
                  ? 'border-primary bg-primary-50 text-primary' :'border-border hover:border-primary-200 bg-white text-text-primary'
              }`}
            >
              <input
                type="checkbox"
                checked={formData.availableDays?.includes(day.id) || false}
                onChange={() => handleArrayToggle('availableDays', day.id)}
                className="sr-only"
              />
              <div className="font-medium text-sm">{day.short}</div>
              <div className="text-xs font-caption mt-1">{day.label}</div>
            </label>
          ))}
        </div>
        
        {errors.availableDays && (
          <p className="text-error text-sm font-caption mt-2 flex items-center space-x-1">
            <Icon name="AlertCircle" size={16} color="currentColor" />
            <span>{errors.availableDays}</span>
          </p>
        )}
      </div>

      {/* Unavailable Dates - Extended to 12 months */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Block Unavailable Dates
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Mark specific dates when you're not available (vacations, existing bookings, etc.). Calendar shows next 12 months.
        </p>

        {/* File Upload for Dates */}
        <div className="bg-surface-50 rounded-xl p-6 mb-6">
          <h4 className="font-medium text-text-primary mb-3 flex items-center space-x-2">
            <Icon name="Upload" size={16} color="#8B4513" />
            <span>Upload Unavailable Dates (Optional)</span>
          </h4>
          <p className="text-sm text-text-secondary font-caption mb-4">
            Upload a file with your unavailable dates (CSV, Excel, or text format)
          </p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,.txt"
            onChange={(e) => handleFileUpload(e.target.files)}
            className="hidden"
          />
          
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconSize={16}
            onClick={triggerFileInput}
            className="cursor-pointer"
          >
            Choose File
          </Button>

          {/* Uploaded Files */}
          {uploadedFiles.length > 0 && (
            <div className="mt-4 space-y-2">
              {uploadedFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div className="flex items-center space-x-3">
                    <Icon name="FileText" size={16} color="#8B4513" />
                    <div>
                      <p className="text-sm font-medium text-text-primary">{file.name}</p>
                      <p className="text-xs text-text-muted">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(file.id)}
                    className="text-error hover:text-error-600 transition-smooth"
                  >
                    <Icon name="Trash2" size={16} color="currentColor" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-6">
          {Object.values(datesByMonth).map((monthData) => (
            <div key={`${monthData.year}-${monthData.month}`}>
              <h4 className="font-heading font-semibold text-lg text-text-primary mb-4">
                {getMonthName(monthData.month)} {monthData.year}
              </h4>
              
              <div className="grid grid-cols-7 gap-2">
                {/* Day headers */}
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-text-muted">
                    {day}
                  </div>
                ))}
                
                {/* Calendar dates */}
                {monthData.dates.map((dateObj) => (
                  <button
                    key={dateObj.date}
                    onClick={() => handleDateToggle(dateObj.date)}
                    className={`p-2 text-sm rounded-lg transition-smooth hover:shadow-soft ${
                      selectedDates.includes(dateObj.date)
                        ? 'bg-error text-error-foreground'
                        : dateObj.isWeekend
                        ? 'bg-secondary-50 text-secondary hover:bg-secondary-100' :'bg-surface-50 text-text-primary hover:bg-surface-100'
                    }`}
                  >
                    {dateObj.day}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Selected Dates Summary with Remove Functionality */}
        {selectedDates.length > 0 && (
          <div className="mt-4 p-4 bg-surface-50 rounded-lg">
            <p className="text-sm font-medium text-text-primary mb-3">
              Blocked Dates ({selectedDates.length}):
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedDates.slice(0, 10).map((date) => (
                <span
                  key={date}
                  className="inline-flex items-center px-2 py-1 bg-error-50 text-error text-xs rounded-full font-data"
                >
                  {new Date(date).toLocaleDateString()}
                  <button
                    onClick={() => removeDateFromSelection(date)}
                    className="ml-1 hover:text-error-600"
                  >
                    <Icon name="X" size={12} color="currentColor" />
                  </button>
                </span>
              ))}
              {selectedDates.length > 10 && (
                <span className="px-2 py-1 bg-surface-100 text-text-muted text-xs rounded-full">
                  +{selectedDates.length - 10} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Working Hours */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Typical Working Hours
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          What are your usual working hours for events?
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Start Time
            </label>
            <Input
              type="time"
              value={formData.workingHoursStart || '09:00'}
              onChange={(e) => handleInputChange('workingHoursStart', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              End Time
            </label>
            <Input
              type="time"
              value={formData.workingHoursEnd || '22:00'}
              onChange={(e) => handleInputChange('workingHoursEnd', e.target.value)}
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.flexibleHours || false}
              onChange={(e) => handleInputChange('flexibleHours', e.target.checked)}
              className="w-4 h-4 text-primary border-border-dark rounded focus:ring-primary focus:ring-2"
            />
            <span className="text-sm font-medium text-text-primary">
              I'm flexible with working hours based on event needs
            </span>
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t border-border">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconSize={18}
        >
          Back to Portfolio
        </Button>
        
        <Button
          variant="primary"
          size="lg"
          onClick={handleNext}
          iconName="ArrowRight"
          iconPosition="right"
          iconSize={18}
        >
          Continue to Verification
        </Button>
      </div>
    </div>
  );
};

export default AvailabilityForm;